import { TimeLength } from '@twaice-fe/shared/utilities';
import { SafetyState } from '../features/energy-analytics.models';
import { StorageLevel, StorageLevelV2 } from './configs.models';

// TODO: Delete after removing the rest use of CCS system endpoint
export class System {
  id: string;
  name?: string;
  description?: string;
  customerId?: string;
  customerBk?: string;
  rootContainerId?: string;
  systemBk?: string;
}

export class SystemDetails {
  id: string;
  systemTid: string;
  customerBk: string;
  systemBk: string;
  systemName: string;
  storageTimezone: string | null;
  safetySummary: SafetySummary | null;
  healthSummary: HealthSummary | null;
  performanceSummary: PerformanceSummary | null;
  riskSummary: RiskSummary | null;
  warrantyState: WarrantyState | null;
  bessMetadata: BessMetadata | null;
  topology: Topology | null;
  componentCountOnLevels: ComponentLevelCount[];
  newChatMessagesAvailable: boolean;
  pricePerMwh: FinancialValue;
  dateFirstDataAvailable: string | null;
  // Backwards compatible: defaulted to true in SystemsService.fetchSystemDetails() until the API always sends this field.
  isSystemOnboarded: boolean;
}

export interface Topology {
  cellGrossWindowNominalCapacityAh: number;
  cellManufacturer: string;
  chemistry: string;
  totalNCellsInParallelInString: number;
  totalNCellsInSeriesInString: number;
}

export interface TimeRangeInterface {
  id: string;
  value: [Date, Date] | string;
  label: string;
  disabled?: boolean;
}

// To be used only to map between Ant components and TimeRangeInterface
export interface SelectorTimeRange {
  id: string;
  value: number;
  label: string;
  disabled?: boolean;
}

export enum TimeRangeGranularityEnum {
  NONE = 'NONE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export interface SafetySummary {
  safetyState: SafetyState;
}

export interface HealthSummary {
  soh: SohSummary | null;
  throughput: ThroughputSummary | null;
}

interface SohSummary {
  min: number | null;
  max: number | null;
  spread: number | null;
  spreadState: SpreadState | null;
}

interface ThroughputSummary {
  last30DaysEfc: number | null;
  totalEfc: number | null;
}

export enum SpreadState {
  STABLE = 'STABLE',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export interface PerformanceSummary {
  usableEnergyPercentage: number | null;
  usableEnergyState: PerformanceState | null;
  recoverableEnergyWh: number | null;
  imbalanceSummary: ImbalanceSummary | null;
  rteSummary: RTESummary | null;
}

export interface FinancialValue {
  value: number;
  currency: string;
}

export interface ImbalanceSummary {
  componentCount: ImbalanceComponentCount | null;
  imbalanceState: ImbalanceState | null;
  imbalancedComponentsPercentage: number | null;
}

interface ImbalanceComponentCount {
  high: number;
  medium: number;
  balanced: number;
  missing: number;
}

export enum ImbalanceState {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  BALANCED = 'BALANCED',
}

export interface ComponentLevelCount {
  levelBk: StorageLevel | StorageLevelV2;
  count: number;
}

export class BessMetadata {
  locationCountry: string | null;
  locationCity: string | null;
  locationLongitude: number | null;
  locationLatitude: number | null;
  commercialOperationDate: string | null;
  hourRating: number | null;
  installedEnergyCapacityMwh: number | null;
  equipmentEnergyCapacityMwh: number | null;
  contractedEnergyCapacityMwh: number | null;
  nameplateEnergyCapacityMwh: number | null;
  nameplatePowerMw: number | null;
  architectureType: string | null;
  assetOwner: string | null;
  operationAndMaintenance: string | null;
  commercializer: string | null;
  integrator: string | null;
  dataProvider: string | null;
  connectionType: string | null;
  pcsSupplier: string | null;
  containerSupplier: string | null;
  rackSupplier: string | null;
  storageTimezone: string | null;
}

export interface RTESummary {
  rteState: PerformanceState | null;
  rtePercentage: number | null;
}

export enum PerformanceState {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum RiskState {
  HIGH = 'high',
  MEDIUM = 'medium',
  NORMAL = 'normal',
}

export interface RiskSummary {
  riskState: RiskState;
}

export interface TimeResolution {
  value: TimeLength;
  label: string;
  disabled?: boolean;
}

export enum WarrantyState {
  Ok = 'OK',
  Breached = 'BREACHED',
}

export const TIME_RESOLUTION_MAPPING: TimeResolution[] = [
  { value: TimeLength.ZERO, label: 'Raw data', disabled: false },
  { value: TimeLength.SECOND, label: '1s', disabled: false },
  { value: TimeLength.HALF_MINUTE, label: '30s', disabled: false },
  { value: TimeLength.MINUTE, label: '1min', disabled: false },
  { value: TimeLength.QUARTER_HOUR, label: '15min', disabled: false },
  { value: TimeLength.HOUR, label: '1h', disabled: false },
  { value: TimeLength.QUARTER_DAY, label: '6h', disabled: false },
  { value: TimeLength.HALF_DAY, label: '12h', disabled: false },
  { value: TimeLength.DAY, label: '1 day', disabled: false },
  { value: TimeLength.WEEK, label: '1 week', disabled: false },
];
